import React from 'react';
import {View, Linking, StyleSheet, Pressable} from 'react-native';
import Text from '../../../components/Text';
import {Card} from 'react-native-paper';
import {colorSwatches, styles} from '../../../styles';
import {deriveDisplayName, getCurrentUnitLink} from '../../../utils';
import {
  showErrorMessage,
  showInformationMessage,
} from '../../../service/flashMessage';
import ApprovalStatus from '../../../components/ApprovalStatus';
import RequireApproval from '../../../components/RequireApproval';
import IconButton from '../../../components/IconButton';
import {initiateSMS, initiateSendEmail} from '../../App/service';
import isWeb from '../../../utils/isWeb';
import ZoomableImage from '../../../components/ZoomableImage';
import Menu from '../../../components/Menu';
import OverflowMenu from '../../../components/OverflowMenu';
import Ionicons from '@expo/vector-icons/Ionicons';
import formatISOToDisplayDate from '../../../utils/formatISOToDisplayDate';

const PeopleListItem = ({
  unitPeopleLink,
  itemClickHandler,
  currentUser,
  openCalendar,
  onClickApprovalsRequired,
  openPermissions,
  onSelectDelete,
  onSelectEditOwnUnitLink,
  sendBarCode,
  onSelectEdit,
  onAddToContacts,
}) => {
  const imageUrl = unitPeopleLink?.profilePicture
    ? `data:image/jpeg;base64,${unitPeopleLink?.profilePicture}`
    : 'https://avatar-management--avatars.us-west-2.prod.public.atl-paas.net/default-avatar.png';
  const phoneNumber = unitPeopleLink?.person.phone
    ? unitPeopleLink?.person.phone
    : unitPeopleLink.phone;
  const phoneAvailable = !!phoneNumber;
  const email = unitPeopleLink?.person.email
    ? unitPeopleLink?.person.email
    : unitPeopleLink.email;
  const emailAvailable = !!email;
  const isCurrentUser = currentUser === unitPeopleLink?.person?.id;
  const currentUnitLink = getCurrentUnitLink();

  const canEditOwnUnitPeopleLink = isCurrentUser;
  const canModifyUnitPeopleLink = isCurrentUser
    ? unitPeopleLink?.peopleType?.residentCanAdd
    : currentUnitLink?.canManagePeople;
  const linkApproved = unitPeopleLink.unitLinkApproved;

  const isInactive = unitPeopleLink?.unit?.status?.includes("Rented") && unitPeopleLink?.personType?.toLowerCase().includes("owner")

  // Calculate age from dateOfBirth
  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const age = unitPeopleLink?.dateOfBirth ? calculateAge(unitPeopleLink.dateOfBirth) : null;
  const isMinor = age !== null && age < 21;

  return (
    <Card
      style={[
        localStyles.card,
        !linkApproved && {backgroundColor: 'gray'},
      ]}
      onPress={() => { }}>
      <View style={localStyles.container}>
        <ZoomableImage
          title={deriveDisplayName(unitPeopleLink)}
          style={styles.userImage}
          source={{
            uri: imageUrl,
          }}
        />
        <View style={localStyles.infoContainer}>
          <View style={localStyles.nameContainer}>
            <Text style={localStyles.nameText}>
              {deriveDisplayName(unitPeopleLink)}
            </Text>
            {isCurrentUser && (
              <View style={styles.currentUser}>
                <Text style={localStyles.currentUserText} numberOfLines={1}>
                  You
                </Text>
              </View>
            )}
            {isMinor && (
              <View style={localStyles.minorBadge}>
                <Text style={localStyles.minorText} numberOfLines={1}>
                  MINOR
                </Text>
              </View>
            )}
          </View>
          <Text style={localStyles.profileNameText}>
            {`Company - ${unitPeopleLink?.company || 'Not provided'}`}
          </Text>
          <Text style={localStyles.peopleTypeText}>
            {unitPeopleLink?.peopleType?.name?.toUpperCase()}
          </Text>
          <ApprovalStatus unitPeopleLink={unitPeopleLink} />
          {unitPeopleLink?.dateOfBirth && (
            <Text style={localStyles.linkedProfileText}>
              {`DOB: ${formatISOToDisplayDate(unitPeopleLink.dateOfBirth)}`}
            </Text>
          )}
          <Text style={localStyles.linkedProfileText}>
            {unitPeopleLink?.person?.id
              ? 'Linked profile'
              : 'No linked profile'}
          </Text>
          <View style={localStyles.dateFrameContainer}>
            <View
              style={[
                localStyles.dateFrame,
                {
                  backgroundColor: unitPeopleLink.dateFrameDay.allowedToday
                    ? 'green'
                    : 'red',
                },
              ]}>
              <Text style={localStyles.dateFrameText} numberOfLines={1}>
                Today
              </Text>
            </View>
            <View
              style={[
                localStyles.dateFrame,
                {
                  backgroundColor: unitPeopleLink.dateFrameDay.allowedTomorrow
                    ? 'green'
                    : 'red',
                },
              ]}>
              <Text style={localStyles.dateFrameText} numberOfLines={1}>
                Tomorrow
              </Text>
            </View>
          </View>
        </View>
        <View style={localStyles.menuContainer}>
          <OverflowMenu
            trigger={triggerProps => {
              return (
                <Pressable
                  {...triggerProps}>
                  <Ionicons
                    name="ellipsis-vertical"
                    iconColor={'#718096'}
                    size={24}
                  />
                </Pressable>
              );
            }}>
            <Menu.Item
              onPress={() => {
                unitPeopleLink?.person?.id
                  ? itemClickHandler()
                  : showInformationMessage("This person doesn't have a profile");
              }}
            >
              Profile
            </Menu.Item>
            <Menu.Item
              onPress={() => {
                if (!phoneAvailable) {
                  showErrorMessage('PeopleItem:  There is no phone number for this person, ask them to add it to their profile or type it yourself');
                  return;
                }
                Linking.openURL(`tel:${phoneNumber}`);
              }}
            >
              Call
            </Menu.Item>
            {!isWeb && (
              <Menu.Item
                onPress={() => {
                  if (!phoneAvailable) {
                    showErrorMessage('PeopleItem - 2:  There is no phone number for this person, ask them to add it to their profile or type it yourself');
                    return;
                  }
                  initiateSMS(phoneNumber, '');
                }}
              >
                Send Text
              </Menu.Item>
            )}
            <Menu.Item
              onPress={() => {
                if (!emailAvailable) {
                  showErrorMessage('PeopleItem - 3:  There is no email for this person, ask them to add it to their profile or type it yourself');
                  return;
                }
                initiateSendEmail(email, '', '');
              }}
            >
              Send Email
            </Menu.Item>
            {!isInactive && canModifyUnitPeopleLink && (
              <Menu.Item
                onPress={() => {
                  onSelectEdit();
                }}
              >
                Edit
              </Menu.Item>
            )}
            {!isInactive && (
              <Menu.Item
                onPress={() => {
                  openCalendar();
                }}
              >
                Calendar
              </Menu.Item>
            )}
            {!isInactive && (
              <Menu.Item
                onPress={() => {
                  openPermissions();
                }}
              >
                Permissions
              </Menu.Item>
            )}
            {!isInactive && (
              <Menu.Item
                onPress={() => {
                  if (!emailAvailable && !phoneAvailable) {
                    showErrorMessage('PeopleItem - 4: Can\'t send code. No phone number/email.');
                    return;
                  } else if (!currentUnitLink.allowedToAuthorizeEntry) {
                    showErrorMessage('PeopleItem - 5: You don\'t have permission to send code.');
                    return;
                  }
                  sendBarCode();
                }}
              >
                Send QR Code
              </Menu.Item>
            )}
            <Menu.Item
              onPress={() => {
                onAddToContacts();
              }}
            >
              Add to contacts
            </Menu.Item>
            {!isInactive && canModifyUnitPeopleLink && (
              <Menu.Item
                onPress={() => {
                  setTimeout(() => {
                    onSelectDelete();
                  }, 1000);
                }}
              >
                Remove from unit
              </Menu.Item>
            )}
          </OverflowMenu>
        </View>
      </View>
      <RequireApproval
        approvals={unitPeopleLink.approvals}
        onClickApprovalsRequired={() => onClickApprovalsRequired()}
      />
    </Card>
  );
};

const localStyles = StyleSheet.create({
  card: {
    margin: 8,
  },
  container: {
    flexDirection: 'row',
    padding: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoContainer: {
    flex: 1,
    marginLeft: 8,
    flexDirection: 'column',
    justifyContent: 'space-around',
  },
  nameContainer: {
    flexDirection: 'row',
  },
  nameText: {
    fontSize: 18,
    flex: 1,
  },
  currentUserText: {
    fontSize: 12,
    paddingHorizontal: 8,
    marginHorizontal: 4,
  },
  minorBadge: {
    backgroundColor: colorSwatches.red[400],
    borderRadius: 100,
    paddingHorizontal: 8,
    marginHorizontal: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  minorText: {
    fontSize: 8,
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  profileNameText: {
    flex: 1,
  },
  peopleTypeText: {
    fontSize: 12,
    marginVertical: 4,
  },
  linkedProfileText: {
    fontSize: 12,
    marginVertical: 4,
  },
  dateFrameContainer: {
    flexDirection: 'row',
    marginVertical: 4,
  },
  dateFrame: {
    borderRadius: 9999,
    marginHorizontal: 4,
  },
  dateFrameText: {
    fontSize: 12,
    color: 'white',
    paddingHorizontal: 4,
    marginRight: 4,
  },
  menuContainer: {
    marginTop: 12,
    marginRight: 16,
  },
});

export default PeopleListItem;
